import { styled } from '@mui/material/styles';
import { Box, Typography, TextField, Checkbox, Radio, Select } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Container
export const FilterContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '32px',
  paddingBottom: '32px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  border: '1px solid #d9d9d9',
  width: '100%',
  position: 'relative',
});

// Header
export const FilterHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  position: 'relative',
});

export const FilterTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#262626',
});

export const CollapseIcon = styled(KeyboardArrowUpIcon, {
  shouldForwardProp: prop => prop !== 'isCollapsed',
})(({ isCollapsed }) => ({
  color: '#737373',
  cursor: 'pointer',
  transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease',
}));

// Content
export const FilterContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'isCollapsed',
})(({ isCollapsed }) => ({
  display: isCollapsed ? 'none' : 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
}));

// Search Section
export const SearchInputWrapper = styled(Box)({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  // Wrapper(인풋 + 검색버튼) 영역 어디에 hover해도 자식 입력창의 외곽선 색을 teal로 바꾼다.
  '&:hover .MuiOutlinedInput-root fieldset': {
    borderColor: '#58B9C1',
  },
});

export const SearchInput = styled(TextField)({
  width: '320px',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#D9D9D9',
    },
    '&:hover fieldset': {
      borderColor: '#58B9C1',
      borderWidth: '2px',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#58B9C1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '15px !important',
    padding: '10px 16px',
    '&::placeholder': {
      color: '#A6A6A6',
      opacity: 1,
    },
  },
});

// Filter Row - 2 columns (회원 구분, 운동 종목)
export const FilterRowTwo = styled(Box)({
  display: 'flex',
  gap: '64px',
  width: '100%',
});

// 묶음(회원 정보·회원권·방문·출결·기간) — 회원목록 상세필터 전용. 다른 화면과 공유하는 행·칸 스타일은 그대로 둔다
export const FilterGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  padding: '20px 24px 24px',
  backgroundColor: '#FAFBFC',
  border: '1px solid #EDEFF2',
  borderRadius: '12px',
  width: '100%',
});

export const FilterGroupTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#1F2329',
  paddingLeft: '10px',
  borderLeft: '3px solid #58B9C1',
  lineHeight: '18px',
});

// 접을 수 있는 묶음의 본문 — 접어도 언마운트하지 않고 숨긴다(입력값 유지)
export const FilterGroupBody = styled(Box, {
  shouldForwardProp: prop => prop !== 'open',
})(({ open }) => ({
  display: open ? 'flex' : 'none',
  flexDirection: 'column',
  gap: '20px',
  width: '100%',
}));

// Filter Row - 3 columns
export const FilterRowThree = styled(Box)({
  display: 'flex',
  gap: '64px',
  width: '100%',
});

// Filter Section
export const FilterSectionFlex = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const FilterSectionFixed = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: 'calc((100% - 128px) / 3)',
  minWidth: '300px',
});

export const SectionTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '8px',
  borderBottom: '1px solid #D9D9D9',
  width: '100%',
});

export const SectionTitleText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#737373',
  whiteSpace: 'nowrap',
});

export const SectionTitleWithCheckbox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingBottom: '8px',
  borderBottom: '1px solid #D9D9D9',
  width: '100%',
});

// Chips
export const ChipsContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  minHeight: '40px',
  flexWrap: 'wrap',
});

export const FilterChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  padding: '0 12px',
  borderRadius: '999px',
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  ...(isActive
    ? {
        backgroundColor: '#58B9C1',
        color: '#fff',
        border: '1px solid #58B9C1',
      }
    : {
        backgroundColor: '#fff',
        color: '#262626',
        border: '1px solid #D9D9D9',
        '&:hover': {
          borderColor: '#58B9C1',
        },
      }),
}));

// Checkbox & Radio
export const CheckboxContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  minHeight: '40px',
});

export const CheckboxItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const EmptyStateMessage = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  minHeight: '40px',
  fontSize: '14px',
  color: '#A6A6A6',
});

export const StyledCheckbox = styled(Checkbox)({
  padding: 0,
  width: '24px',
  height: '24px',
  '&.Mui-checked': {
    color: '#58B9C1',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
  },
});

export const SmallCheckbox = styled(Checkbox)({
  padding: 0,
  width: '20px',
  height: '20px',
  '&.Mui-checked': {
    color: '#0F7F85',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
});

export const RadioContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  minHeight: '40px',
  flexWrap: 'wrap',
});

export const RadioItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const StyledRadio = styled(Radio)({
  padding: 0,
  width: '24px',
  height: '24px',
  '&.Mui-checked': {
    color: '#58B9C1',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
  },
});

// Custom Input (직접입력)
export const CustomInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const CustomInput = styled(TextField)({
  width: '60px',
  '& .MuiOutlinedInput-root': {
    height: '32px',
    borderRadius: '4px',
    '& fieldset': {
      borderColor: '#D9D9D9',
    },
    '&:hover fieldset': {
      borderColor: '#D9D9D9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#58B9C1',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '6px 8px',
    textAlign: 'center',
  },
});

export const CustomInputSuffix = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
});

// Select
export const StyledSelect = styled(Select)({
  width: '100%',
  height: '40px',
  borderRadius: '8px',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9D9D9',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9D9D9',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
  },
  '& .MuiSelect-select': {
    padding: '10px 16px',
  },
});

// Date Picker
export const DatePickerContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  minHeight: '40px',
});

export const DateSeparator = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  width: '12px',
  textAlign: 'center',
  flexShrink: 0,
});

// Footer Buttons
export const FilterFooter = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '16px',
  width: '100%',
});
